import React from 'react';
import { Dialog, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { EVENT_FEEDBACK_STATUS } from '../mapEventReviews';

export const FEEDBACK_CONFIRM_ACTION = {
  [EVENT_FEEDBACK_STATUS.APPROVED]: 'approve',
  [EVENT_FEEDBACK_STATUS.REJECTED]: 'reject',
  [EVENT_FEEDBACK_STATUS.HIDDEN]: 'hide',
  [EVENT_FEEDBACK_STATUS.DELETED]: 'delete',
};

const UNHIDE_ACTION = 'unhide';

const ACTION_CONFIG = {
  approve: {
    title: 'Approve feedback?',
    getMessage: (name) =>
      `Approve feedback from ${name}? It will be shown in the approved feedback section.`,
    confirmLabel: 'Approve',
    confirmColor: '#22c55e',
    confirmHoverColor: '#16a34a',
    icon: CheckIcon,
    iconBg: '#dcfce7',
    iconColor: '#16a34a',
  },
  reject: {
    title: 'Reject feedback?',
    getMessage: (name) => `Reject feedback from ${name}? It will not be published.`,
    confirmLabel: 'Reject',
    confirmColor: '#ef4444',
    confirmHoverColor: '#dc2626',
    icon: CloseIcon,
    iconBg: '#fef2f2',
    iconColor: '#ef4444',
  },
  hide: {
    title: 'Hide feedback?',
    getMessage: (name) => `Hide feedback from ${name}? It will no longer be visible publicly.`,
    confirmLabel: 'Hide',
    confirmColor: '#6b7280',
    confirmHoverColor: '#4b5563',
    icon: VisibilityOffOutlinedIcon,
    iconBg: '#f3f4f6',
    iconColor: '#6b7280',
  },
  unhide: {
    title: 'Unhide feedback?',
    getMessage: (name) =>
      `Unhide feedback from ${name}? It will be shown again in approved feedback.`,
    confirmLabel: 'Unhide',
    confirmColor: '#2563eb',
    confirmHoverColor: '#1d4ed8',
    icon: VisibilityOutlinedIcon,
    iconBg: '#eff6ff',
    iconColor: '#2563eb',
  },
  delete: {
    title: 'Delete feedback?',
    getMessage: (name) => `Delete feedback from ${name}? This action cannot be undone.`,
    confirmLabel: 'Delete',
    confirmColor: '#ef4444',
    confirmHoverColor: '#dc2626',
    icon: DeleteOutlineIcon,
    iconBg: '#fef2f2',
    iconColor: '#ef4444',
  },
};

export const getFeedbackConfirmActionKey = (targetStatus, { isHiddenItem = false } = {}) => {
  if (targetStatus === EVENT_FEEDBACK_STATUS.APPROVED && isHiddenItem) {
    return UNHIDE_ACTION;
  }

  return FEEDBACK_CONFIRM_ACTION[targetStatus] ?? null;
};

const FeedbackStatusConfirmDialog = ({
  open,
  actionKey,
  item,
  confirming = false,
  onClose,
  onConfirm,
}) => {
  const config = ACTION_CONFIG[actionKey];
  const reviewerName = item?.fullName ?? 'this reviewer';

  if (!config) {
    return null;
  }

  const Icon = config.icon;

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          maxWidth: 400,
          width: '100%',
        },
      }}
    >
      <DialogContent sx={{ pb: 1, pt: 1 }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: config.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <Icon sx={{ fontSize: 32, color: config.iconColor }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {config.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {config.getMessage(reviewerName)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 1, pt: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={confirming}
          sx={{ minWidth: 100, textTransform: 'none' }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={confirming}
          onClick={onConfirm}
          sx={{
            minWidth: 100,
            textTransform: 'none',
            bgcolor: config.confirmColor,
            boxShadow: 'none',
            '&:hover': { bgcolor: config.confirmHoverColor, boxShadow: 'none' },
          }}
        >
          {config.confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackStatusConfirmDialog;
